import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./UserPointsWidget.module.scss";
import { userPointsApi } from "../services/user-points.api";
import type {
  UserPointsLeaderboardItemDto,
  UserPointsMeDto,
} from "../types/user-points.types";

const LEADERBOARD_LIMIT = 10;

function getDisplayName(item: UserPointsLeaderboardItemDto): string {
  const fullName = [item.firstName, item.lastName].filter(Boolean).join(" ").trim();
  return fullName || item.userID;
}

export default function UserPointsWidget() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [myPoints, setMyPoints] = useState<UserPointsMeDto | null>(null);
  const [leaderboard, setLeaderboard] = useState<UserPointsLeaderboardItemDto[]>([]);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const [meResponse, leaderboardResponse] = await Promise.all([
          userPointsApi.getMyPoints(),
          userPointsApi.getLeaderboard(1, LEADERBOARD_LIMIT),
        ]);

        if (cancelled) return;

        setMyPoints(meResponse);
        setLeaderboard(leaderboardResponse.items ?? []);
        setTotalItems(leaderboardResponse.metadata?.totalItems ?? 0);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("[user-points] failed to load:", error);
        }
        if (!cancelled) {
          setMyPoints(null);
          setLeaderboard([]);
          setTotalItems(0);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const leaderboardMetaLabel = useMemo(() => {
    if (totalItems <= LEADERBOARD_LIMIT) {
      return t("userPoints.leaderboard.topCount", { count: totalItems || LEADERBOARD_LIMIT });
    }
    return t("userPoints.leaderboard.topCount", { count: LEADERBOARD_LIMIT });
  }, [t, totalItems]);

  if (isLoading) {
    return (
      <div className={styles.section}>
        <div className={styles.skeletonCard} />
        <div className={styles.skeletonCard} />
      </div>
    );
  }

  return (
    <section className={styles.section}>
      <div className={styles.summaryCard}>
        <span className={styles.summaryEyebrow}>{t("userPoints.summary.eyebrow")}</span>
        <div className={styles.summaryPoints}>
          <span className={styles.summaryPointsValue}>{myPoints?.points ?? 0}</span>
          <span className={styles.summaryPointsLabel}>{t("userPoints.common.points")}</span>
        </div>
        <div className={styles.summaryRank}>
          <span>{t("userPoints.summary.rankLabel")}</span>
          <span>#{myPoints?.rank ?? "-"}</span>
        </div>
        <p className={styles.summaryHint}>{t("userPoints.summary.hint")}</p>
      </div>

      <div className={styles.leaderboardCard}>
        <div className={styles.leaderboardHeader}>
          <div>
            <h2 className={styles.leaderboardTitle}>{t("userPoints.leaderboard.title")}</h2>
            <p className={styles.leaderboardSubtitle}>{t("userPoints.leaderboard.description")}</p>
          </div>
          <span className={styles.leaderboardMeta}>{leaderboardMetaLabel}</span>
        </div>

        {leaderboard.length === 0 ? (
          <div className={styles.emptyState}>{t("userPoints.leaderboard.empty")}</div>
        ) : (
          <div className={styles.leaderboardList}>
            {leaderboard.map((item, index) => (
              <div
                key={item.userID}
                className={[
                  styles.leaderRow,
                  index === 0 ? styles.leaderRowTop1 : "",
                  index === 1 ? styles.leaderRowTop2 : "",
                  index === 2 ? styles.leaderRowTop3 : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div className={styles.leaderRank}>{item.rank}</div>
                <div className={styles.leaderMain}>
                  <span className={styles.leaderName}>{getDisplayName(item)}</span>
                  <span className={styles.leaderInfo}>
                    {(item.title || "-") + " · " + (item.departmentName || "-")}
                  </span>
                </div>
                <div className={styles.leaderPoints}>
                  <span className={styles.leaderPointsValue}>{item.points}</span>
                  <span className={styles.leaderPointsLabel}>{t("userPoints.common.points")}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
